import React, { useState, useEffect, useRef } from "react";
import useQuizAttempts from "../../hooks/useQuizAttempts";
import { LA_MODULES, LA_EXPANSION_MODULES } from "../../data/laModules";
import SubmitToLeaderboard from "../../components/SubmitToLeaderboard";
import "../dashboard/Leaderboard.css";
import "./PractiseSection.css";

// Practice banks are fetched on demand so the full question library does not ship
// in the main bundle. Different selections may search one or more banks.
const BANK_LOADERS = {
  calcAg: () =>
    import("../../data/calcAgPracticeBank").then(
      (m) => m.CALC_AG_PRACTICE_BANK,
    ),

  mv: () => import("../../data/mvPracticeBank").then((m) => m.MV_PRACTICE_BANK),

  la: () => import("../../data/laPracticeBank").then((m) => m.LA_PRACTICE_BANK),

  ps: () => import("../../data/psPracticeBank").then((m) => m.PS_PRACTICE_BANK),
};

const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const COURSE_FILTERS = [
  ["all", "All courses"],
  ["calcAg", "Calculus & Geometry"],
  ["mv", "Multivariable Calculus"],
  ["la", "Linear Algebra"],
  ["ps", "Probability & Statistics"],
];

const TOPICS = [
  "Lagrange Multipliers",
  "Divergence & Curl",
  "Stokes' Theorem",
  "Green's Theorem",
  "Taylor & Maclaurin Series",
  "Maclaurin Series",
  "Taylor Series for Multivariable Functions",
  "Partial Derivatives",
  "Vector Calculus",
  "Limits and Continuity",
  "Differentiation",
  "Integration",
  "Sequences and Infinite Series",
  "Conic Sections and Analytic Geometry",
  "3D Analytic Geometry & Vectors",
  "Space Curves & Advanced Multivariable Mappings",
  "Coordinate Transformations & Surfaces",
  "Constrained & Unconstrained Optimization",
  "Vector Fields & Approximation Theory",
  "Potential Theory",
  "Differential Forms",
  "Advanced Integration & Variational Methods",
  "Geometry of Surfaces",
  "Surface Metrics and the First Fundamental Form",
  "Surface Curvature and Geodesics",
  "Integration on Moving Domains",
  "Coarea Formula and Level-Set Integration",
  "Reynolds Transport Theorem",
  "Nonlinear Dynamical Systems",
  "Phase Planes and Local Stability",
  "Lyapunov Stability Methods",
  "2D Lines & Systems of Lines",
  "Circles & Conic Tangents",
  "Advanced Single-Variable Calculus",
  "Ordinary Differential Equations (ODEs)",
  "Space Curves (Frenet-Serret)",
  "Vector-Valued Functions & Motion in Space",
  "Parametric Surfaces",
  "Polar Coordinate Calculus",
  "Solids of Revolution",
  "Volume by Cross-Sections",
  "Numerical Methods",
  "Improper Integrals — Advanced Convergence Tests",
  "Complex Numbers & De Moivre's Theorem",
  "Hyperbolic Functions",
  "Laplace Transforms",
  "Fourier Series",
  "Gamma & Beta Functions",
  "Differentiation Under the Integral Sign (Leibniz Rule)",
  "Fourier Transform & Convolution",
  "Z-Transform & Difference Equations",
  "Introduction to PDEs (Heat & Wave Equations)",
  "Orthogonal Polynomials (Legendre, Chebyshev, Hermite)",
  "Pointwise and Uniform Convergence",
  "Interchanging Limits, Derivatives and Integrals",
  "Power-Series Solutions at Ordinary Points",
  "Frobenius Method at Regular Singular Points",
  "Analytic Functions and Cauchy-Riemann Equations",
  "Contour Integrals and Residues",
  "Multiple Integrals",
  "Vectors & Vector Spaces",
  "Matrices & Determinants",
  "Systems of Linear Equations",
  "Fundamental Subspaces & Rank-Nullity",
  "Eigenvalues & Eigenvectors",
  "Linear Transformations",
  "Orthogonality & Least Squares",
  "Singular Value Decomposition",
  ...[...LA_MODULES, ...LA_EXPANSION_MODULES].flatMap((module) =>
    module.topics.map((topic) => topic.title),
  ),
  "Probability Basics",
  "Random Variables & Distributions",
  "Descriptive Statistics",
  "Hypothesis Testing",
  "Regression & Correlation",
  "Probability Theory & Random Variables",
  "Mathematical Statistics & Inference",
  "Entropy and Mutual Information",
  "Cross-Entropy and KL Divergence",
  "Censoring and Kaplan-Meier Estimation",
  "Hazard Functions and Survival Models",
  "Sequential Probability Ratio Tests",
  "Multiple Testing and False Discoveries",
];

/*
 * Unified Practice Arena selection rules.
 *
 * type: "topic"
 *   → filters by question.topic
 *
 * type: "topicAlias"
 *   → filters by canonicalTopic
 *
 * type: "module"
 *   → filters by question.module
 *
 * banks:
 *   → one or more practice banks are searched
 */
export const PRACTICE_SELECTIONS = {
  ...Object.fromEntries(
    [
      "Vectors & Vector Spaces",
      "Matrices & Determinants",
      "Systems of Linear Equations",
      "Fundamental Subspaces & Rank-Nullity",
      "Eigenvalues & Eigenvectors",
      "Linear Transformations",
      "Orthogonality & Least Squares",
      "Singular Value Decomposition",
    ].map((topic) => [topic, { type: "topic", banks: ["la"] }]),
  ),

  // ============================================================
  // LINEAR ALGEBRA — preserve dynamic existing modules
  // ============================================================

  ...Object.fromEntries(
    [...LA_MODULES, ...LA_EXPANSION_MODULES].flatMap((module) =>
      module.topics.map((topic) => [
        topic.title,
        {
          type: "topic",
          banks: ["la"],
        },
      ]),
    ),
  ),

  // ============================================================
  // CALCULUS / ANALYTICAL GEOMETRY
  // ============================================================

  "Limits and Continuity": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Limits & Continuity": {
    type: "topicAlias",
    canonicalTopic: "Limits and Continuity",
    banks: ["calcAg"],
  },

  Differentiation: {
    type: "topic",
    banks: ["calcAg"],
  },

  "Differentiation & Applications": {
    type: "topicAlias",
    canonicalTopic: "Differentiation",
    banks: ["calcAg"],
  },

  Integration: {
    type: "topic",
    banks: ["calcAg"],
  },

  "Integration & Techniques": {
    type: "topicAlias",
    canonicalTopic: "Integration",
    banks: ["calcAg"],
  },

  "Sequences and Infinite Series": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Sequences, Series & Taylor": {
    type: "topicAlias",
    canonicalTopic: "Sequences and Infinite Series",
    banks: ["calcAg"],
  },

  "Conic Sections and Analytic Geometry": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Conics & 2D Analytic Geometry": {
    type: "topicAlias",
    canonicalTopic: "Conic Sections and Analytic Geometry",
    banks: ["calcAg"],
  },

  "3D Analytic Geometry & Vectors": {
    type: "topic",
    banks: ["calcAg", "mv"],
  },

  "2D Lines & Systems of Lines": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Circles & Conic Tangents": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Advanced Single-Variable Calculus": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Ordinary Differential Equations (ODEs)": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Space Curves (Frenet-Serret)": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Vector-Valued Functions & Motion in Space": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Parametric Surfaces": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Polar Coordinate Calculus": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Solids of Revolution": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Volume by Cross-Sections": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Numerical Methods": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Improper Integrals — Advanced Convergence Tests": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Complex Numbers & De Moivre's Theorem": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Hyperbolic Functions": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Laplace Transforms": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Fourier Series": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Gamma & Beta Functions": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Differentiation Under the Integral Sign (Leibniz Rule)": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Fourier Transform & Convolution": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Z-Transform & Difference Equations": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Introduction to PDEs (Heat & Wave Equations)": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Orthogonal Polynomials (Legendre, Chebyshev, Hermite)": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Pointwise and Uniform Convergence": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Interchanging Limits, Derivatives and Integrals": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Power-Series Solutions at Ordinary Points": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Frobenius Method at Regular Singular Points": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Analytic Functions and Cauchy-Riemann Equations": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Contour Integrals and Residues": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Taylor Series for Multivariable Functions": {
    type: "topic",
    banks: ["calcAg"],
  },

  "Taylor & Maclaurin Series": {
    type: "topicAlias",
    banks: ["calcAg"],
    canonicalTopic: "Taylor Series for Multivariable Functions",
  },

  "Maclaurin Series": {
    type: "topicAlias",
    banks: ["calcAg"],
    canonicalTopic: "Taylor Series for Multivariable Functions",
  },

  // ============================================================
  // MULTIVARIABLE CALCULUS — EXISTING TOPICS
  // ============================================================

  "Partial Derivatives": {
    type: "topic",
    banks: ["mv"],
  },

  "Vector Calculus": {
    type: "topic",
    banks: ["mv"],
  },

  "Multiple Integrals": {
    type: "topic",
    banks: ["mv"],
  },

  "Lagrange Multipliers": {
    type: "topic",
    banks: ["mv"],
  },

  "Divergence & Curl": {
    type: "topic",
    banks: ["mv"],
  },

  "Stokes' Theorem": {
    type: "topic",
    banks: ["mv"],
  },

  /*
   * Green's Theorem is allowed to search both banks.
   * This preserves old questions and also finds any new
   * Green's Theorem questions added to the MVC bank.
   */
  "Green's Theorem": {
    type: "topic",
    banks: ["calcAg", "mv"],
  },

  "Space Curves & Advanced Multivariable Mappings": {
    type: "topic",
    banks: ["mv"],
  },

  // ============================================================
  // NEW MVC MODULE A
  // ============================================================

  "Coordinate Transformations & Surfaces": {
    type: "module",
    banks: ["mv"],
    moduleName: "Coordinate Transformations & Surfaces",
  },
  "Constrained & Unconstrained Optimization": {
    type: "module",
    banks: ["mv"],
    moduleName: "Constrained & Unconstrained Optimization",
  },
  "Vector Fields & Approximation Theory": {
    type: "module",
    banks: ["mv"],
    moduleName: "Vector Fields & Approximation Theory",
  },

  // ============================================================
  // NEW MVC MODULES — Potential Theory, Differential Forms,
  // Advanced Integration & Variational Methods
  // ============================================================

  "Potential Theory": {
    type: "module",
    banks: ["mv"],
    moduleName: "Potential Theory",
  },

  "Differential Forms": {
    type: "module",
    banks: ["mv"],
    moduleName: "Differential Forms",
  },

  "Advanced Integration & Variational Methods": {
    type: "module",
    banks: ["mv"],
    moduleName: "Advanced Integration & Variational Methods",
  },

  // ============================================================
  // MVC — GEOMETRY OF SURFACES (whole module + its two topics)
  // ============================================================

  "Geometry of Surfaces": {
    type: "module",
    banks: ["mv"],
    moduleName: "Geometry of Surfaces",
  },

  "Surface Metrics and the First Fundamental Form": {
    type: "topic",
    banks: ["mv"],
  },

  "Surface Curvature and Geodesics": {
    type: "topic",
    banks: ["mv"],
  },

  // ============================================================
  // MVC — INTEGRATION ON MOVING DOMAINS (whole module + its two topics)
  // ============================================================

  "Integration on Moving Domains": {
    type: "module",
    banks: ["mv"],
    moduleName: "Integration on Moving Domains",
  },

  "Coarea Formula and Level-Set Integration": {
    type: "topic",
    banks: ["mv"],
  },

  "Reynolds Transport Theorem": {
    type: "topic",
    banks: ["mv"],
  },

  // ============================================================
  // MVC — NONLINEAR DYNAMICAL SYSTEMS (whole module + its two topics)
  // ============================================================

  "Nonlinear Dynamical Systems": {
    type: "module",
    banks: ["mv"],
    moduleName: "Nonlinear Dynamical Systems",
  },

  "Phase Planes and Local Stability": {
    type: "topic",
    banks: ["mv"],
  },

  "Lyapunov Stability Methods": {
    type: "topic",
    banks: ["mv"],
  },

  // ============================================================
  // FUTURE NEW MVC MODULES
  // Uncomment these when their practice banks are created.
  // ============================================================

  // "Constrained & Unconstrained Optimization": {
  //   type: "module",
  //   banks: ["mv"],
  //   moduleName: "Constrained & Unconstrained Optimization",
  // },

  // "Vector Fields & Approximation Theory": {
  //   type: "module",
  //   banks: ["mv"],
  //   moduleName: "Vector Fields & Approximation Theory",
  // },

  // ============================================================
  // PROBABILITY & STATISTICS
  // ============================================================

  "Probability Basics": {
    type: "topic",
    banks: ["ps"],
  },

  "Random Variables & Distributions": {
    type: "topic",
    banks: ["ps"],
  },

  "Descriptive Statistics": {
    type: "topic",
    banks: ["ps"],
  },

  "Hypothesis Testing": {
    type: "topic",
    banks: ["ps"],
  },

  "Regression & Correlation": {
    type: "topic",
    banks: ["ps"],
  },

  "Probability Theory & Random Variables": {
    type: "topic",
    banks: ["ps"],
  },

  "Mathematical Statistics & Inference": {
    type: "topic",
    banks: ["ps"],
  },

  "Entropy and Mutual Information": {
    type: "topic",
    banks: ["ps"],
  },

  "Cross-Entropy and KL Divergence": {
    type: "topic",
    banks: ["ps"],
  },

  "Censoring and Kaplan-Meier Estimation": {
    type: "topic",
    banks: ["ps"],
  },

  "Hazard Functions and Survival Models": {
    type: "topic",
    banks: ["ps"],
  },

  "Sequential Probability Ratio Tests": {
    type: "topic",
    banks: ["ps"],
  },

  "Multiple Testing and False Discoveries": {
    type: "topic",
    banks: ["ps"],
  },
};

/*
 * Module dropdown in the topic picker.
 * Each entry lists the practice selections (keys of PRACTICE_SELECTIONS) that belong
 * to one module, in the order they should appear. "course" matches COURSE_FILTERS.
 * To add a module: add its selections to TOPICS and PRACTICE_SELECTIONS, then list it here.
 */
const PRACTICE_MODULES = [
  // Multivariable Calculus
  {
    name: "Coordinate Transformations & Surfaces",
    course: "mv",
    selections: ["Coordinate Transformations & Surfaces"],
  },
  {
    name: "Constrained & Unconstrained Optimization",
    course: "mv",
    selections: ["Constrained & Unconstrained Optimization"],
  },
  {
    name: "Vector Fields & Approximation Theory",
    course: "mv",
    selections: ["Vector Fields & Approximation Theory"],
  },
  { name: "Potential Theory", course: "mv", selections: ["Potential Theory"] },
  {
    name: "Differential Forms",
    course: "mv",
    selections: ["Differential Forms"],
  },
  {
    name: "Advanced Integration & Variational Methods",
    course: "mv",
    selections: ["Advanced Integration & Variational Methods"],
  },
  {
    name: "Geometry of Surfaces",
    course: "mv",
    selections: [
      "Geometry of Surfaces",
      "Surface Metrics and the First Fundamental Form",
      "Surface Curvature and Geodesics",
    ],
  },
  {
    name: "Integration on Moving Domains",
    course: "mv",
    selections: [
      "Integration on Moving Domains",
      "Coarea Formula and Level-Set Integration",
      "Reynolds Transport Theorem",
    ],
  },
  {
    name: "Nonlinear Dynamical Systems",
    course: "mv",
    selections: [
      "Nonlinear Dynamical Systems",
      "Phase Planes and Local Stability",
      "Lyapunov Stability Methods",
    ],
  },
  // Linear Algebra modules (topics come from laModules)
  ...[...LA_MODULES, ...LA_EXPANSION_MODULES]
    .filter((module) => module.title && module.topics?.length)
    .map((module) => ({
      name: module.title,
      course: "la",
      selections: module.topics.map((topic) => topic.title),
    })),
];

const MODULE_SELECTION_LABELS = Object.fromEntries(
  PRACTICE_MODULES.filter((module) => module.selections.length > 1).map(
    (module) => [module.name, `${module.name} — all topics`],
  ),
);

function shuffled(list) {
  const out = [...list];

  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }

  return out;
}

function shuffleQuestionOptions(question) {
  const choices = question.options.map((option, originalIndex) => ({
    option,
    originalIndex,
  }));

  const randomizedChoices = shuffled(choices);

  return {
    ...question,
    options: randomizedChoices.map((choice) => choice.option),
    correctAnswer: randomizedChoices.findIndex(
      (choice) => choice.originalIndex === question.correctAnswer,
    ),
  };
}

export default function PractiseSection() {
  const { record: recordAnswer, restart: restartRecording } = useQuizAttempts();
  // ============================================================
  // LAYER 1: DIFFICULTY SELECTION
  // ============================================================

  const [chosenDifficulty, setChosenDifficulty] = useState(null);

  // ============================================================
  // LAYER 2: TOPIC / MODULE SELECTION
  // ============================================================

  const [chosenTopic, setChosenTopic] = useState(() => {
    const fromLink = new URLSearchParams(window.location.search).get("topic");
    return fromLink && PRACTICE_SELECTIONS[fromLink] ? fromLink : null;
  });
  const [topicQuery, setTopicQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");

  // Modules offered in the dropdown for the current course filter.
  const availableModules = PRACTICE_MODULES.filter(
    (module) => courseFilter === "all" || module.course === courseFilter,
  );
  const activeModule =
    availableModules.find((module) => module.name === moduleFilter) || null;

  const matchesQuery = (topic) =>
    topic.toLowerCase().includes(topicQuery.trim().toLowerCase());

  const visibleTopics = activeModule
    ? activeModule.selections.filter(
        (topic) => PRACTICE_SELECTIONS[topic] && matchesQuery(topic),
      )
    : TOPICS.filter(
        (topic) =>
          matchesQuery(topic) &&
          (courseFilter === "all" ||
            PRACTICE_SELECTIONS[topic]?.banks.includes(courseFilter)),
      );

  const handleCourseFilter = (value) => {
    setCourseFilter(value);
    const stillAvailable = PRACTICE_MODULES.some(
      (module) =>
        module.name === moduleFilter &&
        (value === "all" || module.course === value),
    );
    if (!stillAvailable) setModuleFilter("all");
  };

  // ============================================================
  // CORE GAMEPLAY STATE
  // ============================================================

  const [poolProblems, setPoolProblems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);

  // ============================================================
  // RUNNING SCORE PERSISTENCE
  // ============================================================

  const [score, setScore] = useState(() => {
    const saved = localStorage.getItem("arena_score_tracker");
    return saved
      ? JSON.parse(saved)
      : {
          correct: 0,
          total: 0,
        };
  });

  useEffect(() => {
    localStorage.setItem("arena_score_tracker", JSON.stringify(score));
  }, [score]);

  const [isLoadingBank, setIsLoadingBank] = useState(false);

  // ============================================================
  // QUIZ BANK LOADING / FILTERING
  // ============================================================

  useEffect(() => {
    if (!chosenDifficulty || !chosenTopic) {
      return undefined;
    }

    let cancelled = false;

    const selection = PRACTICE_SELECTIONS[chosenTopic] || {
      type: "topic",
      banks: [],
    };

    setIsLoadingBank(true);
    setPoolProblems([]);
    setCurrentIndex(0);
    setIsQuizCompleted(false);
    resetQuizTurn();

    const selectedLoaders = (selection.banks || [])
      .map(
        (bankName) =>
          BANK_LOADERS[bankName] &&
          (() =>
            BANK_LOADERS[bankName]().then((questions) =>
              questions.map((question) => ({ ...question, bankName })),
            )),
      )
      .filter(Boolean);

    Promise.all(selectedLoaders.map((loader) => loader()))
      .then((banks) => {
        if (cancelled) {
          return;
        }

        // Combine all requested banks.
        const combinedBank = banks.flat();

        let filtered = [];

        // ========================================================
        // MODULE-BASED QUESTIONS
        // ========================================================

        if (selection.type === "module") {
          filtered = combinedBank.filter((question) => {
            return (
              question.difficulty === chosenDifficulty &&
              question.module === selection.moduleName
            );
          });
        } else {
          // ======================================================
          // TOPIC-BASED QUESTIONS
          // ======================================================

          const targetTopic =
            selection.type === "topicAlias"
              ? selection.canonicalTopic
              : chosenTopic;

          filtered = combinedBank.filter((question) => {
            if (question.difficulty !== chosenDifficulty) {
              return false;
            }

            return question.topic === targetTopic;
          });
        }

        // ========================================================
        // REMOVE DUPLICATES BY QUESTION ID
        // ========================================================

        const uniqueQuestions = Array.from(
          new Map(
            filtered.map((question) => [
              `${question.bankName}:${question.id}`,
              question,
            ]),
          ).values(),
        );

        // ========================================================
        // SHUFFLE QUESTIONS + OPTIONS
        // ========================================================

        restartRecording();
        setPoolProblems(shuffled(uniqueQuestions).map(shuffleQuestionOptions));
      })
      .catch((error) => {
        console.error("Failed to load practice question bank:", error);

        if (!cancelled) {
          setPoolProblems([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingBank(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [restartRecording, chosenDifficulty, chosenTopic]);

  // ============================================================
  // AUTO-ADVANCE TIMER
  // ============================================================

  const autoAdvanceTimerRef = useRef(null);

  const resetQuizTurn = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    setSelectedAnswer(null);
    setIsSubmitted(false);
  };

  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  // ============================================================
  // RESET SELECTION
  // ============================================================

  const handleSelectionReset = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    setChosenDifficulty(null);
    setTopicQuery("");
    setCourseFilter("all");
    setModuleFilter("all");
    setChosenTopic(null);
    setPoolProblems([]);
    setCurrentIndex(0);
    setIsQuizCompleted(false);
    resetQuizTurn();
  };

  // ============================================================
  // NEXT QUESTION
  // ============================================================

  const handleNextQuestion = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    if (currentIndex < poolProblems.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      resetQuizTurn();
    } else {
      setIsQuizCompleted(true);
    }
  };

  // ============================================================
  // OPTION CLICK
  // ============================================================

  const recordPracticeAnswer = (question, selectedIndex) => {
    const courseIds = {
      la: "linear-algebra",
      calcAg: "calculus-analytical-geometry",
      mv: "multivariable-calculus",
      ps: "probability-statistics",
    };
    recordAnswer({
      source: "practice",
      courseId: courseIds[question.bankName] || null,
      quizId: `practice:${chosenTopic}:${chosenDifficulty}`,
      questionId: question.id,
      responseId: `${question.bankName}:${question.id}`,
      prompt: question.question,
      options: question.options,
      selectedIndex,
      correctIndex: question.correctAnswer,
      topic: question.topic,
      difficulty: question.difficulty,
    });
  };

  const handleAnswerClick = (index) => {
    if (isSubmitted) {
      return;
    }

    setSelectedAnswer(index);

    const currentProblem = poolProblems[currentIndex];

    if (!currentProblem) {
      return;
    }

    recordPracticeAnswer(currentProblem, index);
    const correct = index === currentProblem.correctAnswer;

    setScore((prev) => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }));

    setIsSubmitted(true);

    // Correct answer automatically advances after confirmation delay.
    if (correct) {
      autoAdvanceTimerRef.current = setTimeout(() => {
        if (currentIndex < poolProblems.length - 1) {
          setCurrentIndex((prev) => prev + 1);
          resetQuizTurn();
        } else {
          setIsQuizCompleted(true);
        }
      }, 750);
    }
  };

  // ============================================================
  // SUBMIT BUTTON
  // ============================================================

  const handleSubmit = () => {
    if (selectedAnswer === null || isSubmitted) {
      return;
    }

    const currentProblem = poolProblems[currentIndex];

    if (!currentProblem) {
      return;
    }

    recordPracticeAnswer(currentProblem, selectedAnswer);
    const correct = selectedAnswer === currentProblem.correctAnswer;

    setScore((prev) => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }));

    setIsSubmitted(true);

    if (correct) {
      autoAdvanceTimerRef.current = setTimeout(() => {
        if (currentIndex < poolProblems.length - 1) {
          setCurrentIndex((prev) => prev + 1);
          resetQuizTurn();
        } else {
          setIsQuizCompleted(true);
        }
      }, 750);
    }
  };

  const currentProblem = poolProblems[currentIndex] || null;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="practice-page">
      <div className="practice-hud">
        <div>
          <h1>Focused Practice Arena</h1>

          <p>
            Comprehensive testing workspace for Advanced Calculus and
            Mathematics modules.
          </p>
        </div>

        <div className="practice-score">
          <div>
            <span className="practice-score-label">Total Score</span>

            <div className="practice-score-value">
              {score.correct} <span>/</span> {score.total}
            </div>
          </div>

          <button
            type="button"
            className="practice-reset"
            onClick={() =>
              setScore({
                correct: 0,
                total: 0,
              })
            }
          >
            Reset
          </button>
        </div>
      </div>

      {/* ======================================================
          DIFFICULTY SELECTION
      ====================================================== */}

      {!chosenDifficulty && (
        <div className="practice-panel">
          <h2>Select Targeted Practice Tier</h2>

          <p
            style={{
              margin: 0,
              color: "var(--muted)",
              fontSize: "0.9rem",
            }}
          >
            Choose a difficulty tier to unlock the specific topic modules.
          </p>

          <div className="practice-tier-grid">
            {DIFFICULTIES.map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => setChosenDifficulty(level)}
                className={`practice-tier-btn practice-tier-btn--${level.toLowerCase()}`}
              >
                {level} Mode
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ======================================================
          TOPIC / MODULE SELECTION
      ====================================================== */}

      {chosenDifficulty && !chosenTopic && (
        <div className="practice-panel">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            <span className="practice-crumb">
              Difficulty Tier:{" "}
              <span className="practice-crumb-pill">{chosenDifficulty}</span>
            </span>

            <button
              type="button"
              className="practice-back"
              onClick={handleSelectionReset}
            >
              ← Back to Tiers
            </button>
          </div>

          <h3>Select Practice Topic</h3>

          <div className="practice-topic-filters">
            <label htmlFor="practice-topic-search">
              Search practice topics
            </label>
            <input
              id="practice-topic-search"
              type="search"
              value={topicQuery}
              onChange={(event) => setTopicQuery(event.target.value)}
              placeholder="For example: PCA or vectors"
            />
            <div
              role="group"
              aria-label="Filter topics by course"
              className="practice-course-filters"
            >
              {COURSE_FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="practice-tool-btn"
                  aria-pressed={courseFilter === value}
                  onClick={() => handleCourseFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {availableModules.length > 0 && (
              <div className="practice-module-filter">
                <label htmlFor="practice-module-select">Module</label>
                <select
                  id="practice-module-select"
                  className="practice-module-select"
                  value={activeModule ? activeModule.name : "all"}
                  onChange={(event) => setModuleFilter(event.target.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    maxWidth: "28rem",
                    marginTop: "0.35rem",
                    padding: "0.55rem 0.75rem",
                    borderRadius: "8px",
                    border: "1px solid var(--rule, #e2e8f0)",
                    background: "var(--surface, transparent)",
                    color: "inherit",
                    font: "inherit",
                  }}
                >
                  <option value="all">All modules and topics</option>
                  {availableModules.map((module) => (
                    <option key={module.name} value={module.name}>
                      {module.name}
                      {module.selections.length > 1
                        ? ` (${module.selections.length - (module.selections.includes(module.name) ? 1 : 0)} topics)`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <p role="status">{visibleTopics.length} topics available</p>
            {visibleTopics.length === 0 && (
              <p>No topics match. Clear the search or choose another course.</p>
            )}
            {(topicQuery ||
              courseFilter !== "all" ||
              moduleFilter !== "all") && (
              <button
                type="button"
                className="practice-tool-btn"
                onClick={() => {
                  setTopicQuery("");
                  setCourseFilter("all");
                  setModuleFilter("all");
                }}
              >
                Clear topic filters
              </button>
            )}
          </div>
          <div className="practice-topic-grid">
            {visibleTopics.map((topic) => (
              <button
                key={topic}
                type="button"
                className="practice-topic-btn"
                onClick={() => setChosenTopic(topic)}
              >
                {MODULE_SELECTION_LABELS[topic] || topic}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ======================================================
          QUIZ WORKSPACE
      ====================================================== */}

      {chosenDifficulty && chosenTopic && (
        <div>
          <div className="practice-toolbar">
            <div className="practice-crumb">
              <span className="practice-crumb-pill">{chosenDifficulty}</span>

              <span>/</span>

              <span
                style={{
                  color: "var(--ink)",
                }}
              >
                {chosenTopic}
              </span>
            </div>

            <div className="practice-toolbar-actions">
              <button
                type="button"
                className="practice-tool-btn"
                onClick={handleSelectionReset}
              >
                Change Topic
              </button>
            </div>
          </div>

          <div className="practice-panel">
            {!isQuizCompleted && currentProblem ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <p className="practice-kicker" style={{ margin: 0 }}>
                    Question Workspace
                  </p>

                  <span
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "bold",
                      color: "var(--accent)",
                    }}
                  >
                    Question {currentIndex + 1} of {poolProblems.length}
                  </span>
                </div>

                <h2 className="practice-question">{currentProblem.question}</h2>

                <div
                  className="practice-options"
                  role="listbox"
                  aria-label="Answer choices"
                >
                  {currentProblem.options.map((option, idx) => {
                    let stateClass = "";

                    if (selectedAnswer === idx && !isSubmitted) {
                      stateClass = "practice-option--selected";
                    }

                    if (isSubmitted) {
                      if (idx === currentProblem.correctAnswer) {
                        stateClass = "practice-option--correct";
                      } else if (selectedAnswer === idx) {
                        stateClass = "practice-option--wrong";
                      } else {
                        stateClass = "practice-option--muted";
                      }
                    }

                    return (
                      <button
                        key={idx}
                        type="button"
                        role="option"
                        aria-selected={selectedAnswer === idx}
                        disabled={isSubmitted}
                        onClick={() => handleAnswerClick(idx)}
                        className={`practice-option ${stateClass}`.trim()}
                      >
                        <span className="practice-option__letter">
                          {String.fromCharCode(65 + idx)}
                        </span>

                        <span className="practice-option__text">{option}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="practice-actions">
                  {!isSubmitted ? (
                    <button
                      type="button"
                      className="practice-submit"
                      onClick={handleSubmit}
                      disabled={selectedAnswer === null}
                    >
                      Submit Verification
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="practice-next"
                      onClick={handleNextQuestion}
                    >
                      {currentIndex < poolProblems.length - 1
                        ? "Next Question →"
                        : "Finish Quiz & View Score"}
                    </button>
                  )}
                </div>

                {isSubmitted && (
                  <div className="practice-insight">
                    <h4>Solution Insight</h4>

                    <p>{currentProblem.explanation}</p>
                  </div>
                )}
              </div>
            ) : isQuizCompleted ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "1rem 0",
                }}
              >
                <h2>Quiz Complete!</h2>

                <p>
                  You have finished all questions for{" "}
                  <strong>{chosenTopic}</strong> ({chosenDifficulty}).
                </p>

                <div
                  style={{
                    margin: "1.5rem 0",
                  }}
                >
                  <SubmitToLeaderboard
                    quizId={`practice-${chosenTopic}-${chosenDifficulty}`}
                    score={score.correct}
                    total={Math.max(score.total, 1)}
                  />
                </div>

                <button
                  type="button"
                  className="practice-tool-btn practice-tool-btn--accent"
                  onClick={handleSelectionReset}
                  style={{
                    marginTop: "1rem",
                  }}
                >
                  Choose Another Topic
                </button>
              </div>
            ) : isLoadingBank ? (
              <div className="practice-empty">Loading question bank…</div>
            ) : (
              <div className="practice-empty">
                No questions populated matching this configuration choice.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
